import {
  foodReservationDetailsTemplate,
  serviceReservationDetailsTemplate,
} from '@xeetr/mail/templates'
import { TenantType } from '@xeetr/types/enums'
import { Settings } from '@xeetr/types/models'
import { useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'

import { Button } from '@/components/ui/button'

import {
  COMMON_EMAIL_VARIABLES,
  COMPONENT_VARIABLES,
  EMAIL_TEMPLATE_CONFIGS,
  EMAIL_TEMPLATE_DEFAULTS,
  EmailTemplate,
} from '@/components/settings/emails/constants'
import {
  usePlaceholderReplacement,
  useTemplateEditStatus,
} from '@/components/settings/emails/hooks'
import { TemplateEditor } from '@/components/settings/emails/TemplateEditor'
import { TemplateList } from '@/components/settings/emails/TemplateList'
import { VariablesList } from '@/components/settings/emails/VariablesList'

const Emails = () => {
  const { control, watch, formState } = useFormContext<Settings>()
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null)
  const [showPreview, setShowPreview] = useState(false)

  const tenantType = watch('tenant.type')
  const tenantBookingCustomFields = watch('tenant.settings.bookingCustomFields')

  const tenantSpecificPartTemplate =
    tenantType === TenantType.FOOD
      ? foodReservationDetailsTemplate
      : serviceReservationDetailsTemplate

  const emailTemplates: EmailTemplate[] = useMemo(
    () => [
      ...EMAIL_TEMPLATE_CONFIGS.map((template) => ({
        ...template,
        defaultBody: EMAIL_TEMPLATE_DEFAULTS[template.key],
      })),
      {
        key: 'tenant-specific-part',
        name: 'Reservierungsdetails',
        bodyField: 'customTenantSpecificPart',
        defaultSubject: '',
        defaultBody: tenantSpecificPartTemplate,
        type: 'component',
        tenantType: 'all',
      },
    ],
    [tenantSpecificPartTemplate]
  )

  const filteredEmailTemplates = useMemo(
    () =>
      emailTemplates.filter(
        (template) => template.tenantType === 'all' || template.tenantType === tenantType
      ),
    [emailTemplates, tenantType]
  )

  const isTemplateEdited = useTemplateEditStatus(watch, formState)

  const currentBody = selectedTemplate
    ? watch(`tenant.settings.${selectedTemplate.bodyField}` as any) || selectedTemplate.defaultBody
    : ''

  const currentTenantSpecificPart =
    watch('tenant.settings.customTenantSpecificPart' as any) || tenantSpecificPartTemplate

  const replacePlaceholders = usePlaceholderReplacement({
    watch,
    currentTenantSpecificPart,
  })

  const previewHtml = useMemo(() => {
    if (!selectedTemplate) return ''
    const contentToPreview = currentBody
    return replacePlaceholders(contentToPreview)
  }, [selectedTemplate, currentBody, currentTenantSpecificPart, replacePlaceholders])

  const customerEmails = filteredEmailTemplates.filter(
    (t) => t.type === 'email' && t.recipient === 'customer'
  )
  const tenantEmails = filteredEmailTemplates.filter(
    (t) => t.type === 'email' && t.recipient === 'tenant'
  )
  const componentList = filteredEmailTemplates.filter((t) => t.type === 'component')

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Emails- und Komponentenvorlagen</h2>
        <p className="text-muted-foreground text-sm">
          Passen Sie die E-Mail- und Komponenten-Vorlagen an, die an Kunden und Ihr Unternehmen
          gesendet werden.
        </p>
      </div>
      <div className="flex flex-col gap-4 lg:h-full lg:flex-row">
        <div className="w-full lg:w-1/3">
          <TemplateList
            templates={customerEmails}
            selectedTemplate={selectedTemplate}
            isTemplateEdited={isTemplateEdited}
            onSelectTemplate={setSelectedTemplate}
            title="Kunden-E-Mails (an Kunden gesendet)"
          />
          <TemplateList
            templates={tenantEmails}
            selectedTemplate={selectedTemplate}
            isTemplateEdited={isTemplateEdited}
            onSelectTemplate={setSelectedTemplate}
            title="Unternehmens-E-Mails (an Unternehmen gesendet)"
          />
          <TemplateList
            templates={componentList}
            selectedTemplate={selectedTemplate}
            isTemplateEdited={isTemplateEdited}
            onSelectTemplate={setSelectedTemplate}
            title="Komponenten"
          />
        </div>
        <div className="w-full lg:w-2/3">
          {selectedTemplate && (
            <>
              <div className="mb-4">
                <h3 className="mb-2 text-lg font-semibold">{selectedTemplate.name}</h3>
                <Button onClick={() => setShowPreview(!showPreview)} className="mb-2">
                  {showPreview ? 'Bearbeiten' : 'Vorschau'}
                </Button>
              </div>
              {showPreview ? (
                <div
                  className="max-h-full overflow-y-auto border bg-white p-4 text-center"
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                />
              ) : (
                <>
                  <TemplateEditor
                    template={selectedTemplate}
                    control={control}
                    label={
                      selectedTemplate.type === 'component'
                        ? 'Benutzerdefinierte Komponenten Vorlage (HTML)'
                        : 'Benutzerdefinierte E-Mail Vorlage (HTML)'
                    }
                    rows={selectedTemplate.type === 'component' ? 8 : 10}
                  />
                  <VariablesList
                    variables={
                      selectedTemplate.type === 'component'
                        ? COMPONENT_VARIABLES
                        : [...COMMON_EMAIL_VARIABLES, ...(selectedTemplate.variables || [])]
                    }
                    customFields={
                      selectedTemplate.type === 'component' ? tenantBookingCustomFields : undefined
                    }
                  />
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default Emails
